/**
 * -- Custom preloading strategy to preload modules based on public / private domain --
 *
 * This replaces Angular's PreloadAllModules strategy to allow for more control over which modules are preloaded.
 * When using the public domain, we don't need to preload private modules and vice versa.
 *
 * Each lazily loaded module at the top level app-routing.module.ts files should be tagged with a preload property:
 *
 * 'always' - always preload the module (should be used for modules shared between public and private)
 * 'public' - preload the module only when using the public domain
 * 'private' - preload the module only when using a private domain
 *
 * Children of these modules do not need to be tagged as they will be preloaded when the parent module is preloaded.
 * If a module is not tagged, it will not be preloaded.
 *
 * If desired, additional preloading granularity can be achieved by tagging any lazily loaded module/component with a data.preload property:
 */

import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { environment } from 'environments/environment';
import { Observable, EMPTY } from 'rxjs';
import { PreloadType } from 'app/shared/_models/preload-type.model';

@Injectable({
	providedIn: 'root'
})
export class CustomPreloadingStrategy implements PreloadingStrategy {
	preload(route: Route, load: () => Observable<any>): Observable<any> {
		if (shouldPreload(route)) {
			return load();
		} else {
			return EMPTY;
		}
	}
}

function shouldPreload(route: Route): boolean {
	// hostname check determines if public or private
	if (window.location.hostname === environment.publicDomain) {
		// public domain in use - preload public module & children
		if ([PreloadType.Always, PreloadType.Public].includes(route.data?.preload) || !route.data?.preload) {
			return true;
		}
	} else {
		// private domain in use - preload private module & children
		if ([PreloadType.Always, PreloadType.Private].includes(route.data?.preload) || !route.data?.preload) {
			return true;
		}
	}
	return false;
}
